/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

/* eslint-disable perfectionist/sort-named-imports */
import type { MBeaconEventContent, M_BEACON } from './beacon';
import type { ReactionEventContent, RoomMessageEventContent, StickerEventContent } from './events';
import type { Membership } from './membership';
import { UnstableValue } from '../utils/NamespacedValue';

// Event
export enum ClientEvent {
  /**
   * Fires whenever the SDK's syncing state is updated. The state can be one of:
   * <ul>
   *
   * <li>PREPARED: The client has synced with the server at least once and is
   * ready for methods to be called on it. This will be immediately followed by
   * a state of SYNCING. <i>This is the equivalent of "syncComplete" in the
   * previous API.</i></li>
   *
   * <li>CATCHUP: The client has detected the connection to the server might be
   * available again and will now try to do a sync again. As this sync might take
   * a long time (depending how long ago was last synced, and general server
   * performance) the client is put in this mode so the UI can reflect trying
   * to catch up with the server after losing connection.</li>
   *
   * <li>SYNCING : The client is currently polling for new events from the server.
   * This will be called <i>after</i> processing latest events from a sync.</li>
   *
   * <li>ERROR : The client has had a problem syncing with the server. If this is
   * called <i>before</i> PREPARED then there was a problem performing the initial
   * sync. If this is called <i>after</i> PREPARED then there was a problem polling
   * the server for updates. This may be called multiple times even if the state is
   * already ERROR. <i>This is the equivalent of "syncError" in the previous
   * API.</i></li>
   *
   * <li>RECONNECTING: The sync connection has dropped, but not (yet) in a way that
   * should be considered erroneous.
   * </li>
   *
   * <li>STOPPED: The client has stopped syncing with server due to stopClient
   * being called.
   * </li>
   * </ul>
   * State transition diagram:
   * ```
   *                                          +---->STOPPED
   *                                          |
   *              +----->PREPARED -------> SYNCING <--+
   *              |                        ^  |  ^    |
   *              |      CATCHUP ----------+  |  |    |
   *              |        ^                  V  |    |
   *   null ------+        |  +------- RECONNECTING   |
   *              |        V  V                       |
   *              +------->ERROR ---------------------+
   *
   * NB: 'null' will never be emitted by this event.
   *
   * ```
   * Transitions:
   * <ul>
   *
   * <li>`null -> PREPARED` : Occurs when the initial sync is completed
   * first time. This involves setting up filters and obtaining push rules.
   *
   * <li>`null -> ERROR` : Occurs when the initial sync failed first time.
   *
   * <li>`ERROR -> PREPARED` : Occurs when the initial sync succeeds
   * after previously failing.
   *
   * <li>`PREPARED -> SYNCING` : Occurs immediately after transitioning
   * to PREPARED. Starts listening for live updates rather than catching up.
   *
   * <li>`SYNCING -> RECONNECTING` : Occurs when the live update fails.
   *
   * <li>`RECONNECTING -> RECONNECTING` : Can occur if the update calls
   * continue to fail, but the keepalive calls (to /versions) succeed.
   *
   * <li>`RECONNECTING -> ERROR` : Occurs when the keepalive call also fails
   *
   * <li>`ERROR -> SYNCING` : Occurs when the client has performed a
   * live update after having previously failed.
   *
   * <li>`ERROR -> ERROR` : Occurs when the client has failed to keepalive
   * for a second time or more.</li>
   *
   * <li>`SYNCING -> SYNCING` : Occurs when the client has performed a live
   * update. This is called <i>after</i> processing.</li>
   *
   * <li>`* -> STOPPED` : Occurs once the client has stopped syncing or
   * trying to sync after stopClient has been called.</li>
   * </ul>
   *
   * The payloads consists of the following 3 parameters:
   *
   * - state - An enum representing the syncing state. One of "PREPARED",
   * "SYNCING", "ERROR", "STOPPED".
   *
   * - prevState - An enum representing the previous syncing state.
   * One of "PREPARED", "SYNCING", "ERROR", "STOPPED" <b>or null</b>.
   *
   * - data - Data about this transition.
   *
   * @example
   * ```ts
   * matrixClient.on("sync", function(state, prevState, data) {
   *   switch (state) {
   *     case "ERROR":
   *       // update UI to say "Connection Lost"
   *       break;
   *     case "SYNCING":
   *       // update UI to remove any "Connection Lost" message
   *       break;
   *     case "PREPARED":
   *       // the client instance is ready to be queried.
   *       var rooms = matrixClient.getRooms();
   *       break;
   *   }
   * });
   * ```
   */
  Sync = 'sync',
  /**
   * Fires whenever the SDK receives a new event.
   * <p>
   * This is only fired for live events received via /sync - it is not fired for
   * events received over context, search, or pagination APIs.
   *
   * The payload is the matrix event which caused this event to fire.
   * @example
   * ```
   * matrixClient.on("event", function(event){
   *   var sender = event.getSender();
   * });
   * ```
   */
  Event = 'event',

  /**
   * Fires whenever new user-scoped account_data is added.
   * The payload is a pair of event ({@link XHubChatEvent}) describing the account_data just added, and the previous event, if known:
   *  - event: The event describing the account_data just added
   *  - oldEvent: The previous account data, if known.
   * @example
   * ```
   * matrixClient.on("accountData", function(event, oldEvent){
   *   myAccountData[event.type] = event.content;
   * });
   * ```
   */
  AccountData = 'accountData',
  /**
   * Fires whenever a new Room is added. This will fire when you are invited to a
   * room, as well as when you join a room. <strong>This event is experimental and
   * may change.</strong>
   *
   * The payload is the newly created room, fully populated.
   * @example
   * ```
   * matrixClient.on("Room", function(room){
   *   var roomId = room.roomId;
   * });
   * ```
   */
  Room = 'Room',
  /**
   * Fires whenever a Room is removed. This will fire when you forget a room.
   * <strong>This event is experimental and may change.</strong>
   * The payload is the roomId of the deleted room.
   * @example
   * ```
   * matrixClient.on("deleteRoom", function(roomId){
   *   // update UI from getRooms()
   * });
   * ```
   */
  DeleteRoom = 'deleteRoom',
  SyncUnexpectedError = 'sync.unexpectedError',

  // Realtime bridge events (Centrifuge)
  RealtimeConnected = 'realtime.connected',
  RealtimeDisconnected = 'realtime.disconnected',
  RealtimeReconnecting = 'realtime.reconnecting',
  RealtimeError = 'realtime.error',
  RealtimeNewMessage = 'realtime.message.new',
  RealtimeRoomUpdated = 'realtime.room.updated',
  RealtimePresence = 'realtime.presence',
}
/**
 * Marker event type to point back at imported historical content in a room. See
 * [MSC2716](https://github.com/matrix-org/matrix-spec-proposals/pull/2716).
 * Note that this reference is UNSTABLE and subject to breaking changes,
 * including its eventual removal.
 */
export const UNSTABLE_MSC2716_MARKER = new UnstableValue('m.room.marker', 'org.matrix.msc2716.marker');

export enum XHubChatEventEvent {
  /**
   * An event has been decrypted, or we have failed to decrypt it.
   *
   * The payload consists of:
   *
   *  `event` - The {@link MatrixEvent} which we attempted to decrypt.
   *
   *  `err` - The error that occurred during decryption, or `undefined` if no error occurred.
   *     Avoid use of this: {@link MatrixEvent.decryptionFailureReason} is more useful.
   */
  Decrypted = 'Event.decrypted',
  VisibilityChange = 'Event.visibilityChange',

  BeforeRedaction = 'Event.beforeRedaction',
  LocalEventIdReplaced = 'Event.localEventIdReplaced',
  Status = 'Event.status',
  RelationsCreated = 'Event.relationsCreated',
  SentinelUpdated = 'Event.sentinelUpdated',
}

/**
 * Enum for event statuses.
 * @readonly
 */
export enum EventStatus {
  /** The event was not sent and will no longer be retried. */
  NOT_SENT = 'not_sent',

  /** The event is in the process of being sent. */
  SENDING = 'sending',

  /** The event is in a queue waiting to be sent. */
  QUEUED = 'queued',

  /** The event has been sent to the server, but we have not yet received the echo. */
  SENT = 'sent',

  /** The event was cancelled before it was successfully sent. */
  CANCELLED = 'cancelled',
}

export enum MsgType {
  Text = 'm.text',
  Emote = 'm.emote',
  Notice = 'm.notice',
  Image = 'm.image',
  File = 'm.file',
  Audio = 'm.audio',
  Location = 'm.location',
  Video = 'm.video',
}

export enum RelationType {
  Annotation = 'm.annotation',
  Reference = 'm.reference',
}

export type IEventRelation = {
  'rel_type'?: RelationType | string;
  'event_id'?: string;
  'is_falling_back'?: boolean;
  'm.in_reply_to'?: {
    event_id?: string;
  };
  'key'?: string;
};

export type IMentions = {
  user_ids?: string[];
  room?: boolean;
};

export type IContent = {
  [key: string]: any;
  'msgtype'?: MsgType | string;
  'avatar_url'?: string;
  'displayname'?: string;
  'm.relates_to'?: IEventRelation;

  'm.mentions'?: IMentions;
};

export type ServerMessageRes = {
  messages: IEvent[];
  total: number;
};
export type ServerCommentRes = {
  comments: Comment[];
  total: number;
  has_next: boolean;
  next_cursor?: string;
};

export type Comment = {
  id: string;
  parent_comment_id: string | null;
  post_id: string;
  commenter_id: string;
  commenter: Commenter;
  content: string;
  likes: number;
  replies: number;
  reply_to_comment: Comment | null;
  reply_to_comment_id: string | null;
  created_at: string;
  updated_at: string;
  liked: boolean;
  replied_comments: Comment[];
  time_pin?: string | null;
};

export type Commenter = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar: string;
};

export type IUnsigned = {
  [key: string]: any;
  'age'?: number;
  'prev_sender'?: string;
  'prev_content'?: IContent;
  'replaces_state'?: string;
  'transaction_id'?: string;
  'm.relations'?: Record<RelationType | string, any>; // No common pattern for aggregated relations
};

export type IClearEvent = {
  room_id?: string;
  type: string;
  state_key?: string;
  content: Omit<IContent, 'membership' | 'avatar_url' | 'displayname' | 'm.relates_to'>;
  unsigned?: IUnsigned;
};

export enum EventType {
  // Room state events
  RoomCanonicalAlias = 'm.room.canonical_alias',
  RoomCreate = 'm.room.create',
  RoomJoinRules = 'm.room.join_rules',
  RoomMember = 'm.room.member',
  RoomThirdPartyInvite = 'm.room.third_party_invite',
  RoomName = 'm.room.name',
  RoomTopic = 'm.room.topic',
  RoomAvatar = 'm.room.avatar',
  RoomPinnedEvents = 'm.room.pinned_events',
  RoomEncryption = 'm.room.encryption',
  RoomHistoryVisibility = 'm.room.history_visibility',
  RoomGuestAccess = 'm.room.guest_access',
  RoomServerAcl = 'm.room.server_acl',
  RoomTombstone = 'm.room.tombstone',
  RoomPredecessor = 'org.matrix.msc3946.room_predecessor',

  // Moderation policy lists
  PolicyRuleUser = 'm.policy.rule.user',
  PolicyRuleRoom = 'm.policy.rule.room',
  PolicyRuleServer = 'm.policy.rule.server',

  SpaceChild = 'm.space.child',
  SpaceParent = 'm.space.parent',

  // Room timeline events
  RoomRedaction = 'm.room.redaction',
  RoomMessage = 'm.room.message',
  RoomMessageEncrypted = 'm.room.encrypted',
  Sticker = 'm.sticker',
  KeyVerificationRequest = 'm.key.verification.request',
  KeyVerificationStart = 'm.key.verification.start',
  KeyVerificationCancel = 'm.key.verification.cancel',
  KeyVerificationMac = 'm.key.verification.mac',
  KeyVerificationDone = 'm.key.verification.done',
  KeyVerificationKey = 'm.key.verification.key',
  KeyVerificationAccept = 'm.key.verification.accept',
  // Not used directly - see READY_TYPE in VerificationRequest.
  KeyVerificationReady = 'm.key.verification.ready',
  // use of this is discouraged https://matrix.org/docs/spec/client_server/r0.6.1#m-room-message-feedback
  RoomMessageFeedback = 'm.room.message.feedback',
  Reaction = 'm.reaction',
  PollStart = 'org.matrix.msc3381.poll.start',

  // Room ephemeral events
  Typing = 'm.typing',
  Receipt = 'm.receipt',
  Presence = 'm.presence',

  // Room account_data events
  FullyRead = 'm.fully_read',
  Tag = 'm.tag',
  SpaceOrder = 'org.matrix.msc3230.space_order', // MSC3230

  // User account_data events
  PushRules = 'm.push_rules',
  Direct = 'm.direct',
  IgnoredUserList = 'm.ignored_user_list',

  // to_device events
  RoomKey = 'm.room_key',
  RoomKeyRequest = 'm.room_key_request',
  ForwardedRoomKey = 'm.forwarded_room_key',
  Dummy = 'm.dummy',
  SecretRequest = 'm.secret.request',
  SecretSend = 'm.secret.send',
}

/**
 *
 * @field state_key - We have two main event types:
 *  - State events (change name(avatar,...) of room, etc.), which have a state_key (which may be the empty string)
 *  - Message events, which do not have a state_key
 *
 * @example - Response from server
 * {
    "id": "ae03ff89-85ea-4a62-a1f3-080731e57a57",
    "room_id": "ca5190ed-da0a-4537-8884-f1ad93677667",
    "sender_id": "9015335e-c8a2-44ab-914b-1fc34b38dbbb",
    "content": "How are you doing ",
    "type": "text",
    "participants_readed": null,
    "created_at": "2025-10-08T10:32:45.201Z",
    "updated_at": "2025-10-08T10:32:45.201Z",
    "timestamp": "2025-10-08T10:32:45.201Z"
  }
 */
export type IEvent = {
  id: string;
  room_id?: string;
  sender_id: string;
  content: IContent;
  type: string;
  timestamp: number;
  txn_id?: string;
  state_key?: string;
  membership?: Membership;
  unsigned: IUnsigned;
  created_at: string;
  redacts?: string;
  time_pin?: string | null;
};

/**
 * Minimal event structure, for use when we only care about the type and content of an event.
 * Use in cache from indexeddb-local-backend.ts
 */
export type IMinimalEvent = {
  content: IContent;
  type: EventType | string;
  room_id?: string;
  unsigned?: IUnsigned;
};

export type IThreadBundledRelationship = {
  latest_event: IEvent;
  count: number;
  current_user_participated?: boolean;
};

/**
 * https://github.com/matrix-org/matrix-doc/pull/4023
 *
 * @experimental
 */
export const UNSIGNED_THREAD_ID_FIELD = new UnstableValue('thread_id', 'org.matrix.msc4023.thread_id');

/** A reason code for a failure to decrypt an event. */
export enum DecryptionFailureCode {
  /** Message was encrypted with a Megolm session whose keys have not been shared with us. */
  MEGOLM_UNKNOWN_INBOUND_SESSION_ID = 'MEGOLM_UNKNOWN_INBOUND_SESSION_ID',

  /** A special case of {@link MEGOLM_UNKNOWN_INBOUND_SESSION_ID}: the sender has told us it is withholding the key. */
  MEGOLM_KEY_WITHHELD = 'MEGOLM_KEY_WITHHELD',

  /** A special case of {@link MEGOLM_KEY_WITHHELD}: the sender has told us it is withholding the key, because the current device is unverified. */
  MEGOLM_KEY_WITHHELD_FOR_UNVERIFIED_DEVICE = 'MEGOLM_KEY_WITHHELD_FOR_UNVERIFIED_DEVICE',

  /** Message was encrypted with a Megolm session which has been shared with us, but in a later ratchet state. */
  OLM_UNKNOWN_MESSAGE_INDEX = 'OLM_UNKNOWN_MESSAGE_INDEX',

  /**
   * Message was sent before the current device was created; there is no key backup on the server, so this
   * decryption failure is expected.
   */
  HISTORICAL_MESSAGE_NO_KEY_BACKUP = 'HISTORICAL_MESSAGE_NO_KEY_BACKUP',

  /**
   * Message was sent before the current device was created; there was a key backup on the server, but we don't
   * seem to have access to the backup. (Probably we don't have the right key.)
   */
  HISTORICAL_MESSAGE_BACKUP_UNCONFIGURED = 'HISTORICAL_MESSAGE_BACKUP_UNCONFIGURED',

  /**
   * Message was sent before the current device was created; there was a (usable) key backup on the server, but we
   * still can't decrypt. (Either the session isn't in the backup, or we just haven't gotten around to checking yet.)
   */
  HISTORICAL_MESSAGE_WORKING_BACKUP = 'HISTORICAL_MESSAGE_WORKING_BACKUP',

  /**
   * Message was sent when the user was not a member of the room.
   */
  HISTORICAL_MESSAGE_USER_NOT_JOINED = 'HISTORICAL_MESSAGE_USER_NOT_JOINED',

  /**
   * The sender's identity is not verified, but was previously verified.
   */
  SENDER_IDENTITY_PREVIOUSLY_VERIFIED = 'SENDER_IDENTITY_PREVIOUSLY_VERIFIED',

  /**
   * The sender device is not cross-signed.  This will only be used if the
   * device isolation mode is set to `OnlySignedDevicesIsolationMode`.
   */
  UNSIGNED_SENDER_DEVICE = 'UNSIGNED_SENDER_DEVICE',

  /**
   * We weren't able to link the message back to any known device.  This will
   * only be used if the device isolation mode is set to `OnlySignedDevicesIsolationMode`.
   */
  UNKNOWN_SENDER_DEVICE = 'UNKNOWN_SENDER_DEVICE',

  /** Unknown or unclassified error. */
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Mapped type from event type to content type for all specified non-state room events.
 */
export type TimelineEvents = {
  [EventType.RoomMessage]: RoomMessageEventContent;
  [EventType.Sticker]: StickerEventContent;
  [EventType.Reaction]: ReactionEventContent;
  [M_BEACON.name]: MBeaconEventContent;
};

/* REDACT_KEEP_KEYS gives the keys we keep when an event is redacted
 *
 * This is specified here:
 *  http://matrix.org/speculator/spec/HEAD/client_server/latest.html#redactions
 *
 * Also:
 *  - We keep 'unsigned' since that is created by the local server
 *  - We keep user_id for backwards-compat with v1
 */
export const REDACT_KEEP_KEYS = new Set([
  'event_id',
  'type',
  'room_id',
  'user_id',
  'sender',
  'state_key',
  'prev_state',
  'content',
  'unsigned',
  'origin_server_ts',
  'id',
]);

// a map from state event type to the .content keys we keep when an event is redacted
export const REDACT_KEEP_CONTENT_MAP: Record<string, Record<string, 1>> = {
  [EventType.RoomMember]: { membership: 1 },
  [EventType.RoomJoinRules]: { join_rule: 1 },
} as const;

/**
 * A type of message that affects visibility of a message,
 * as per https://github.com/matrix-org/matrix-doc/pull/3531
 *
 * @experimental
 */
export const EVENT_VISIBILITY_CHANGE_TYPE = new UnstableValue('m.visibility', 'org.matrix.msc3531.visibility');

/**
 * When an event is a visibility change event, as per MSC3531,
 * the visibility change implied by the event.
 */
export type IVisibilityChange = {
  /**
   * If `true`, the target event should be made visible.
   * Otherwise, it should be hidden.
   */
  visible: boolean;

  /**
   * The event id affected.
   */
  eventId: string;

  /**
   * Optionally, a human-readable reason explaining why
   * the event was hidden. Ignored if the event was made
   * visible.
   */
  reason: string | null;
};

/**
 * Variant of `MessageVisibility` for the case in which the message should be hidden.
 */
export type IMessageVisibilityHidden = {
  readonly visible: false;
  /**
   * Optionally, a human-readable reason to show to the user indicating why the
   * message has been hidden (e.g. "Message Pending Moderation").
   */
  readonly reason: string | null;
};

/**
 * Variant of `MessageVisibility` for the case in which the message should be displayed.
 */
export type IMessageVisibilityVisible = {
  readonly visible: true;
};

/**
 * Message hiding, as specified by https://github.com/matrix-org/matrix-doc/pull/3531.
 */
export type MessageVisibility = IMessageVisibilityHidden | IMessageVisibilityVisible;

// A singleton implementing `IMessageVisibilityVisible`.
export const MESSAGE_VISIBLE: IMessageVisibilityVisible = Object.freeze({ visible: true });
